import { YearsPickerProps } from "@omnia/shared/components/years/YearsPicker";
import { defineReactComponent } from "@omnia/shared/react/angular";
import { DialogBody } from "@omnia/shared/react/DialogBody";
import { DialogFooter, DialogFooterButtons } from "@omnia/shared/react/DialogFooter";
import { DialogHeader } from "@omnia/shared/react/DialogHeader";
import { Msg } from "@omnia/shared/react/Msg";
import { IOmniaShell, handleApiError, react } from "../../omnia";
import { IFieldsService } from "../../types";
import { getUpdateRequest, hasStateChanged, hasStylesChanged, resolveState } from "../changes";
import { createInitialState, refreshState } from "../state";
import { BasicStyle, BasicStyleWithInitialState, CropStyle, State } from "../types";
import { CopiedStyleContextProvider } from "./CopiedStyleContext";
import { CropRow } from "./CropRow";
import { IconButton } from "./IconButton";

/**
 * Defines the EditCropColours React component to integrate with Angular
 */ 
export function defineEditCropColoursReact() {
    return defineReactComponent({
        inject: {
            shell: "OmniaShell",
            fieldSvc: "OmniaFieldsService",
        },

        start(context: react.ReactAngularComponentContext) {
            const { rootElement, dependencies } = context;

            const shell: IOmniaShell = dependencies["shell"];
            const fieldSvc: IFieldsService = dependencies["fieldSvc"];

            createInitialState(shell.getSession(), () => shell.refresh(false), fieldSvc).then((initialState) => {
                const element = React.createElement(EditCropColours, {
                    ...context,
                    initialState,
                });
                ReactDOM.render(element, rootElement);
            });
        },
    });
}

/**
 * Edit the crop colours, and the crop styles, for the current field
 * @param dependencies: the dependencies injected by Angular
 * @param close: the function to close the dialog
 * @param state: the initial state
 * @returns  the React component
 */
function EditCropColours({
    dependencies,
    close,
    initialState,
}: react.ReactAngularComponentContext & { initialState: State }) {
    const shell: IOmniaShell = dependencies["shell"];
    const fieldSvc: IFieldsService = dependencies["fieldSvc"];

    const [state, setState] = React.useState<State>(initialState);

    const copiedColourState = React.useState<BasicStyle | null>(null);

    const commit = () => {
        const api = state.api;
        const update = getUpdateRequest(state);

        api.upsertStyles(update)
            .then(() => {
                refreshState(state).then((s) => setState((prev) => ({ ...prev, ...s })));
            })
            .catch((e) => handleApiError(shell, e));
    };

    const updateCropStyle = React.useCallback(
        (id: number, action: React.SetStateAction<CropStyle>) => {
            setState((prev) => ({
                ...prev,
                crops: prev.crops.map((c) => (c.cropId === id ? { ...resolveState(c, action) } : c)),
            }));
        },
        [setState]
    );

    const setSelectedYears = React.useCallback(
        (value: number[]) => {
            setState((prev) => ({ ...prev, selectedYears: value }));
        },
        [setState]
    );

    const cropYears: YearsPickerProps["options"] = state.availableYears
        .sort((a, b) => b - a)
        .map((year) => ({
            year,
            selected: state.selectedYears.includes(year),
            required: state.currentYear === year,
        }));

    const hasStyleChanges = hasStylesChanged(state);
    const hasChanges = hasStateChanged(state);

    const applyToAll = React.useCallback(
        (action: <T extends BasicStyleWithInitialState>(style: T) => T) => {
            setState((prev) => ({
                ...prev,
                crops: prev.crops.map((c) => ({
                    ...c,
                    ...action(c),
                    varieties: c.varieties.map((v) => ({
                        ...v,
                        ...action(v),
                    })),
                    purposes: c.purposes.map((p) => ({
                        ...p,
                        ...action(p),
                    })),
                    grasses: c.grasses.map((g) => ({
                        ...g,
                        ...action(g),
                    })),
                })),
            }));
        },
        [setState]
    );

    const handleDeleteAll = React.useCallback(() => {
        applyToAll((foo) => ({ ...foo, color: null, pattern: null }));
    }, [setState]);

    const handleResetAll = React.useCallback(() => {
        applyToAll((foo) => ({ ...foo, ...foo.initialState }));
    }, [setState]);

    return (
        <CopiedStyleContextProvider value={copiedColourState}>
            <div className="omnia-fields-styles">
                <DialogHeader icon="fas fa-paint-roller">
                    <Msg>Edit crop colours</Msg>
                </DialogHeader>
                <DialogBody>
                    <table className="omnia-fields-styles--table">
                        <thead className="sticky">
                            <tr>
                                <th colSpan={2}></th>
                                <th>
                                    <Msg>Type</Msg>
                                </th>
                                <th>
                                    <Msg>Colour</Msg>
                                </th>
                                <th>
                                    <Msg>Pattern</Msg>
                                </th>
                                <th>
                                    <Msg>Style</Msg>
                                </th>
                                <th className="reset">
                                    {hasStyleChanges ? (
                                        <IconButton
                                            onClick={handleResetAll}
                                            icon="fas fa-undo-alt"
                                            title="Revert all unsaved changes"
                                        />
                                    ) : (
                                        <IconButton
                                            onClick={handleDeleteAll}
                                            icon="fas fa-trash"
                                            title="Clear all custom styles"
                                        />
                                    )}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {state.crops.map((cropItem) => (
                                <CropRow
                                    key={cropItem.cropId}
                                    cropItem={cropItem}
                                    setCropItem={(value) => updateCropStyle(cropItem.cropId, value)}
                                />
                            ))}
                        </tbody>
                    </table>
                </DialogBody>
                <DialogFooter>
                    <div className="target-container">
                        <p>Apply styles to:</p>
                        <react.YearsPicker
                            options={cropYears}
                            onChange={(value) => {
                                setSelectedYears(value.filter((x) => x.selected).map((x) => x.year));
                            }}
                            currentYear={state.currentYear}
                        />
                    </div>

                    <DialogFooterButtons
                        hasChanges={hasChanges}
                        canCommit={hasChanges}
                        onClose={close}
                        onCommit={commit}
                    />
                </DialogFooter>
            </div>
        </CopiedStyleContextProvider>
    );
}
